/* global runtime */
// Register-, adresse-, kjørebok- og assistentadaptere.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const fetchJson = async (url, errorMessage) => {
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) {
    if (response.status === 404 || response.status === 410) return null;
    throw new Error(errorMessage || t('bam.registry.lookupFailed'));
  }
  return response.json();
};
const sanitizeOrganizationNumber = (value) => String(value || '').replace(/\D/g, '');

const mapBrregEntity = (entity) => {
  if (!entity) return null;
  const address = entity.forretningsadresse || entity.postadresse || {};
  return {
    source: 'brreg',
    id: String(entity.organisasjonsnummer || ''),
    businessName: String(entity.navn || ''),
    name: String(entity.navn || ''),
    organizationNumber: String(entity.organisasjonsnummer || ''),
    address: safeArray(address.adresse).join(', '),
    postalCode: String(address.postnummer || ''),
    postalPlace: String(address.poststed || ''),
    email: String(entity.epostadresse || entity.epost || ''),
    phone: String(entity.telefon || entity.mobiltelefon || ''),
    companyTypeCode: String(entity.organisasjonsform?.kode || '').toUpperCase(),
    companyTypeLabel: String(entity.organisasjonsform?.beskrivelse || ''),
    // Naeringskoden ligger allerede i svaret fra Enhetsregisteret. Den er grunnlaget
    // for bransjesammenligning senere, og koster ingenting a ta vare pa na.
    industryCode: String(entity.naeringskode1?.kode || ''),
    industryLabel: String(entity.naeringskode1?.beskrivelse || ''),
    employeeCount: Number.isFinite(Number(entity.antallAnsatte)) ? Number(entity.antallAnsatte) : null,
    vatRegistered: typeof entity.registrertIMvaregisteret === 'boolean' ? entity.registrertIMvaregisteret : undefined,
  };
};

const lookupBusinessRegistry = async (query, country = 'NO') => {
  const cleanCountry = String(country || 'NO').toUpperCase();
  const text = String(query || '').trim();
  if (cleanCountry !== 'NO' || text.length < 2) return [];
  const org = sanitizeOrganizationNumber(text);
  if (org.length === 9) {
    const entity = await fetchJson(`https://data.brreg.no/enhetsregisteret/api/enheter/${org}`, t('bam.registry.brregLookupFailed'));
    return entity ? [mapBrregEntity(entity)].filter(Boolean) : [];
  }
  const params = new URLSearchParams({ navn: text, navnMetodeForSoek: 'FORTLOEPENDE', size: '8' });
  const payload = await fetchJson(`https://data.brreg.no/enhetsregisteret/api/enheter?${params.toString()}`, t('bam.registry.brregSearchFailed'));
  return safeArray(payload?._embedded?.enheter).map(mapBrregEntity).filter(Boolean);
};

// Enhetsregisteret har et eget rolleendepunkt per foretak. Det er aapent og
// krever ingen avtale, men gir ingen e-postadresser — de fylles inn i Plumme.
const fetchCompanyRolesFromRegistry = async (organizationNumber, country = 'NO') => {
  if (String(country || 'NO').toUpperCase() !== 'NO') return [];
  const org = sanitizeOrganizationNumber(organizationNumber);
  if (org.length !== 9) throw new Error(t('svc.orgNumberNeeded', 'Foretaket mangler organisasjonsnummer.'));
  const payload = await fetchJson(
    `https://data.brreg.no/enhetsregisteret/api/enheter/${org}/roller`,
    t('svc.registryRolesFailed', 'Kunne ikke hente roller fra Enhetsregisteret.'),
  );
  return window.PlummeCompanyRoles?.mapRegistryRoles?.(payload) || [];
};

const getCompanyRoles = () =>
  window.PlummeCompanyRoles?.normalizePeople?.(mergeState(runtime.appState).companyRoles) || [];

const saveCompanyRoles = async (people = []) => {
  const companyRoles = window.PlummeCompanyRoles?.normalizePeople?.(people) || [];
  await updateStateAndPersist((state) => ({ ...state, companyRoles }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.companyRoles));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return companyRoles;
};

// Henter rollene paa nytt og beholder e-postene brukeren allerede har lagt inn.
const refreshCompanyRolesFromRegistry = async () => {
  const state = mergeState(runtime.appState);
  const fetched = await fetchCompanyRolesFromRegistry(
    state.settings?.organizationNumber,
    state.settings?.country,
  );
  const merged = window.PlummeCompanyRoles?.mergeRegistryRoles?.(state.companyRoles, fetched) || fetched;
  return saveCompanyRoles(merged);
};

const searchAddressLookup = async (query, country = 'NO') => {
  const cleanCountry = String(country || 'NO').toUpperCase();
  const text = String(query || '').trim();
  if (cleanCountry !== 'NO' || text.length < 3) return [];
  const params = new URLSearchParams({ sok: text, treffPerSide: '6' });
  const payload = await fetchJson(`https://ws.geonorge.no/adresser/v1/sok?${params.toString()}`, t('bam.registry.addressLookupFailed'));
  const seen = new Set();
  return safeArray(payload?.adresser).reduce((matches, entry) => {
    const number = typeof entry.nummer === 'number' ? String(entry.nummer) : '';
    const streetFallback = [entry.adressenavn, [number, entry.bokstav].filter(Boolean).join(' ')].filter(Boolean).join(' ').trim();
    const address = String(entry.adressetekstutenadressetilleggsnavn || entry.adressetekst || streetFallback || '').trim();
    const postalCode = String(entry.postnummer || '').trim();
    const postalPlace = String(entry.poststed || '').trim();
    const id = [entry.kommunenummer || '', entry.adressekode || '', address, postalCode, postalPlace].join(':');
    if (!address || !postalCode || !postalPlace || seen.has(id)) return matches;
    seen.add(id);
    matches.push({ id, address, postalCode, postalPlace, municipality: String(entry.kommunenavn || '').trim() });
    return matches;
  }, []);
};

const normalizeMileageAddressSuggestion = (entry) => {
  const item = entry && typeof entry === 'object' ? entry : {};
  const latitude = Number(item.latitude);
  const longitude = Number(item.longitude);
  return {
    id: String(item.id || item.displayName || item.label || ''),
    label: String(item.label || item.displayName || ''),
    displayName: String(item.displayName || item.label || ''),
    latitude,
    longitude,
  };
};

const searchMileageAddresses = async ({ query = '', country } = {}) => {
  const text = String(query || '').trim();
  if (text.length < 3) return [];
  const state = mergeState(runtime.appState);
  const payload = await authorizedPost('searchMileageAddressesHttp', {
    query: text,
    country: country || state.settings.country || 'NO',
  });
  return safeArray(payload.suggestions)
    .map(normalizeMileageAddressSuggestion)
    .filter((entry) =>
      entry.id &&
      entry.label &&
      entry.displayName &&
      Number.isFinite(entry.latitude) &&
      Number.isFinite(entry.longitude),
    );
};

// Koordinater -> adresse. Brukes av GPS-sporingen i kjøreboken til å fylle
// Fra/Til automatisk. Feiler stille: en tur skal kunne føres uten adresseoppslag.
const reverseGeocode = async ({ latitude, longitude } = {}) => {
  const lat = Number(latitude);
  const lon = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return '';
  const payload = await authorizedPost('reverseGeocodeHttp', { latitude: lat, longitude: lon });
  return String(payload.label || payload.displayName || '');
};

const calculateMileageDistance = async ({ fromAddress = '', toAddress = '' } = {}) => {
  const from = String(fromAddress || '').trim();
  const to = String(toAddress || '').trim();
  if (!from || !to) throw new Error(t('svc.missingAddresses'));
  const payload = await authorizedPost('calculateMileageDistanceHttp', { fromAddress: from, toAddress: to });
  const kilometers = Number(payload.kilometers);
  return {
    kilometers: Number.isFinite(kilometers) ? kilometers : 0,
    fromLabel: payload.from?.displayName || payload.from?.label || '',
    toLabel: payload.to?.displayName || payload.to?.label || '',
    source: payload.source || '',
  };
};

const normalizeAssistantReply = (payload) => {
  const lines = safeArray(payload?.lines).filter((line) => typeof line === 'string' && line.trim());
  const answer = typeof payload?.answer === 'string' ? payload.answer.trim() : '';
  return {
    ok: payload?.ok !== false,
    intent: typeof payload?.intent === 'string' ? payload.intent : payload?.metadata?.intentName || 'unknown',
    answer,
    lines,
    text: [answer, ...lines].filter(Boolean).join('\n\n'),
    metadata: payload?.metadata && typeof payload.metadata === 'object' ? payload.metadata : null,
    debugRouteSource: typeof payload?.debugRouteSource === 'string' ? payload.debugRouteSource : undefined,
    debugRouteHandler: typeof payload?.debugRouteHandler === 'string' ? payload.debugRouteHandler : undefined,
  };
};

const askHomeAssistant = async (question, options = {}) => {
  const cleanQuestion = String(question || '').trim();
  if (!cleanQuestion) throw new Error(t('svc.writeQuestion'));
  if (cleanQuestion.length > 2000) throw new Error(t('svc.messageTooLong2000'));
  const ok = await initFirebase();
  if (!ok) throw new Error(t('svc.noFirebaseAssistant'));
  if (!runtime.auth?.currentUser) throw new Error(t('svc.needLoginAssistant'));
  const businessId = options.businessId || runtime.activeBusinessId;
  if (!businessId) throw new Error(t('svc.pickBusiness'));

  const state = mergeState(runtime.appState);
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  const locale = options.locale || uiLocale();
  const requestBody = {
    question: cleanQuestion,
    year,
    locale,
    businessId,
    // Samtalen matte foelge med for at oppfolgingssvar skulle bety noe. Uten
    // den var «Acme AS» bare tre ord uten sporsmal foran seg, og assistenten
    // spurte om det samme paa nytt. Backend kutter uansett selv; dette sparer
    // baandbredde paa veien inn.
    history: safeArray(options.history).slice(-10).map((entry) => ({
      role: entry?.role === 'assistant' ? 'assistant' : 'user',
      content: String(entry?.content || '').slice(0, 1200),
    })),
    pendingIntentName: options.pendingIntentName,
    pendingMissingFields: options.pendingMissingFields,
    pendingExecutionPayload: options.pendingExecutionPayload,
    workflowContext: options.workflowContext,
    allowLegacyRouting: options.allowLegacyRouting === true,
    source: options.source || 'plumme-home',
  };
  const payload = options.onDelta
    ? await authorizedPostStream('askBusinessAssistantHttp', requestBody, { onDelta: options.onDelta, signal: options.signal })
    : await authorizedPost('askBusinessAssistantHttp', requestBody);
  return normalizeAssistantReply(payload);
};

const createAssistantInvoiceDraft = async (payload = {}) => {
  const normalizedInvoice = payload?.normalizedInvoice;
  if (!normalizedInvoice || payload?.action !== 'create_invoice') throw new Error(t('svc.missingInvoiceToSave'));
  const result = await authorizedPost('createAssistantInvoiceDraftCommandHttp', {
    businessId: runtime.activeBusinessId,
    normalizedInvoice,
  });
  if (!result?.invoice) throw new Error(t('svc.backendDidNotSave'));
  replaceInvoiceLocal(result.invoice);
  return result;
};

const askAiAdvisorAssistant = async (question, options = {}) => {
  const cleanQuestion = String(question || '').trim();
  if (!cleanQuestion) throw new Error(t('svc.writeQuestion'));
  if (cleanQuestion.length > 1200) throw new Error(t('svc.messageTooLong1200'));
  const ok = await initFirebase();
  if (!ok) throw new Error(t('svc.noFirebaseAssistant'));
  if (!runtime.auth?.currentUser) throw new Error(t('svc.needLoginAssistant'));
  const state = mergeState(runtime.appState);
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  const locale = options.locale || uiLocale();
  const payload = await authorizedPost('askAiAdvisorAssistantHttp', {
    question: cleanQuestion,
    history: safeArray(options.history).slice(-6).map((entry) => ({
      role: entry?.role === 'assistant' ? 'assistant' : 'user',
      content: String(entry?.content || '').slice(0, 600),
    })),
    year,
    locale,
    businessId: options.businessId || runtime.activeBusinessId || undefined,
  });
  return normalizeAssistantReply(payload);
};

const askPersonalFinanceAssistant = async (question, options = {}) => {
  const cleanQuestion = String(question || '').trim();
  if (!cleanQuestion) throw new Error(t('svc.writeQuestion'));
  if (cleanQuestion.length > 1200) throw new Error(t('svc.messageTooLong1200'));
  const ok = await initFirebase();
  if (!ok) throw new Error(t('svc.noFirebaseAssistant'));
  if (!runtime.auth?.currentUser) throw new Error(t('svc.needLoginAssistant'));
  const state = mergeState(runtime.appState);
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  const locale = options.locale || uiLocale();
  const payload = await authorizedPost('askPersonalFinanceAssistantHttp', {
    question: cleanQuestion,
    // Vinduet speiler PF_CHAT_MAX_HISTORY_* i functions/personalFinance/personalFinancePolicy.ts.
    // Backend kutter uansett selv — dette sparer bare båndbredde på veien inn.
    history: safeArray(options.history).slice(-8).map((entry) => ({
      role: entry?.role === 'user' ? 'user' : 'assistant',
      content: String(entry?.content || '').slice(0, 500),
    })),
    turnCount: Number(options.turnCount) || undefined,
    year,
    locale,
    businessId: options.businessId || runtime.activeBusinessId || undefined,
  });
  return normalizeAssistantReply(payload);
};

const getPersonalFinanceDailyAdvice = async (options = {}) => {
  const ok = await initFirebase();
  if (!ok) throw new Error(t('bam.registry.noFirebaseDailyAdvice'));
  if (!runtime.auth?.currentUser) throw new Error(t('bam.registry.needLoginDailyAdvice'));
  const state = mergeState(runtime.appState);
  const payload = await authorizedPost('getPersonalFinanceDailyAdviceHttp', {
    businessId: options.businessId || runtime.activeBusinessId || undefined,
    year: Number(options.year || state.activeYear) || new Date().getFullYear(),
    locale: options.locale || uiLocale(),
    timeZone: options.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  });
  return payload && typeof payload === 'object' ? payload : { advice: {} };
};

const formatAssistantReplyForChat = (reply) => {
  if (typeof reply === 'string') return reply;
  if (!reply || typeof reply !== 'object') return '';
  if (reply.text) return reply.text;
  return [reply.answer, ...safeArray(reply.lines)].filter(Boolean).join('\n\n');
};

// Speil av deny-listene i firestore.rules (validBusinessAppStateUpdate). Feltene
// eies av Cloud Functions, som skriver dem med admin-SDK utenom reglene.
//
// Klienten lagrer alltid hele `runtime.appState`, og mergeState fyller inn
// standardverdier for felt dokumentet ikke har fra for — for eksempel
// `openingBalanceByYear: {}` for et foretak uten apningsbalanse. For Firestore er
// det en endring av et sperret felt, og da avvises HELE skrivingen. Alt annet i
// samme lagring — arsvalg, kunder, produkter, prosjekter, bakgrunnstema — gikk
// dermed tapt uten annet spor enn `runtime.error`.
//
// Vi utelater feltene i stedet. `set(..., { merge: true })` fletter kart rekursivt,
// sa serverens verdier star urort.
const SERVER_OWNED_STATE_FIELDS = [
  'accountingStartupProfile', 'fixedAssetAutomaticPosting', 'accountingVerifierCancellations', 'accountingVerifierEquity',
  'companyRestMode',
  'investmentRegister',
  'invoices',
  'expenses',
  'otherSales',
  'payments',
  'customerSettlements', 'supplierSettlements',
  'foreignCustomerCredits', 'accountingReverseChargeReturns',
  'customerBadDebtWriteoffs', 'foreignCustomerBadDebtWriteoffs',
  'accountingVerifierFxRates', 'accountingLoans', 'accountingProvisions',
  'accountingPeriodLocks', 'accountingVerifierPeriodLocks',
  'bankStatements',
  'bankMatchGroups',
  'bankReconciliationPostings',
  'mileageEntries',
  'accountingSourceVersions',
  'payrollRuns',
  'aMeldingSubmissions', 'pensionPremiumPayments',
  'payrollTaxPaymentsByYear',
  'customsDeclarations',
  'deadlinesByYear',
  'yearEndEntriesByYear',
  'closedYearsByYear',
  'yearClosingProcessByYear',
  'taxReturnProcessByYear',
  'annualAccountsProcessByYear',
  'openingBalanceByYear',
  'accountingRecordedSeqCounter',
  'accountingEvents',
  'accountingJournalStatus', 'accountingJournalDeferred', 'accountingReadModelCoverage', 'ledgerBalancesByMonth', 'ledgerBalancesStatus',
  'accountingBasisCoverage',
  'entityYearEndReviewByYear',
];
// Regnskapsregimet i `settings` — eies av functions/businessProfileMutationHttp.ts.
const SERVER_OWNED_SETTINGS_FIELDS = [
  'country',
  'countryCode',
  'businessCountry',
  'companyType',
  'companyTypeCode',
  'companyTypeLabel',
  'legalFormCode',
  'organizationForm',
  'smallEnterprise', 'smallEnterpriseDeclaration',
  'fiscalYearStartMonth', 'firstFiscalYearStart', 'firstFiscalYearEnd', 'fiscalYearDeclaration',
  'organizationNumber',
  'orgNumber',
  'vatRegistered',
  'vatPeriod',
  'vatPeriodHistory',
  'vatRegisteredFrom',
  'vatRegisteredFromSource',
  'registeredInForetaksregisteret',
];

const stripServerOwnedState = (state) => {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return state;
  const result = { ...state };
  SERVER_OWNED_STATE_FIELDS.forEach((field) => { delete result[field]; });
  if (result.settings && typeof result.settings === 'object' && !Array.isArray(result.settings)) {
    const settings = { ...result.settings };
    SERVER_OWNED_SETTINGS_FIELDS.forEach((field) => { delete settings[field]; });
    result.settings = settings;
  }
  return result;
};

const serverOwnedStateSignature = (state) => JSON.stringify({
  fields: Object.fromEntries(SERVER_OWNED_STATE_FIELDS.map((field) => [field, state?.[field]])),
  settings: Object.fromEntries(SERVER_OWNED_SETTINGS_FIELDS.map((field) => [field, state?.settings?.[field]])),
});
// Reference check first: an ordinary edit spreads the previous state, so the large
// server-owned lists (invoices, expenses, bankStatements, ...) keep their identity and
// need no JSON.stringify. Only a real difference in reference pays for the signature.
const hasClientServerOwnedStateChange = (previous, next) => {
  const sameReferences = SERVER_OWNED_STATE_FIELDS.every((field) => previous?.[field] === next?.[field])
    && SERVER_OWNED_SETTINGS_FIELDS.every((field) => previous?.settings?.[field] === next?.settings?.[field]);
  return !sameReferences && serverOwnedStateSignature(previous) !== serverOwnedStateSignature(next);
};
